import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Send, XCircle, Plus, CreditCard, Trash2, RefreshCw, Download, Mail, Pencil } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATUS_BADGE = {
  draft: "bg-[#111827] text-slate-300",
  sent: "bg-blue-50 text-blue-700",
  partially_paid: "bg-amber-50 text-amber-800",
  paid: "bg-emerald-50 text-emerald-800",
  overdue: "bg-rose-50 text-rose-800",
  voided: "bg-[#1F2937] text-gray-500",
};

const fmtMoney = (n, ccy = "GBP") => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
  } catch {
    return `${ccy} ${(n || 0).toFixed(2)}`;
  }
};

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: "",
    payment_method: "Wire Transfer",
    transaction_reference: "",
    payment_date: new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const [xeroSyncing, setXeroSyncing] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: "", to_name: "", message: "" });
  const [sendBusy, setSendBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);

  useEffect(() => {
    api.get("/integrations/xero/status").then(r => setXeroConnected(r.data?.connected)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}`);
      setInvoice(data);
    } catch {
      toast.error("Invoice not found");
      navigate("/invoices");
    }
  }, [invoiceId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!invoice) return <div className="p-12 text-sm text-gray-500">Loading invoice…</div>;

  const ccy = invoice.currency || "GBP";

  const transition = async (lifecycle_status) => {
    try {
      const { data } = await api.patch(`/invoices/${invoiceId}`, { lifecycle_status });
      setInvoice(data);
      toast.success(lifecycle_status === "sent" ? "Invoice sent" : `Invoice ${lifecycle_status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update invoice");
    }
  };

  const recordPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/payments`, {
        amount: Number(payForm.amount),
        payment_method: payForm.payment_method,
        transaction_reference: payForm.transaction_reference,
        payment_date: payForm.payment_date,
      });
      setInvoice(data);
      toast.success("Payment recorded");
      setPayOpen(false);
      setPayForm({ ...payForm, amount: "", transaction_reference: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  const voidPayment = async (paymentId) => {
    if (!window.confirm("Reverse this payment? The invoice balance will be recalculated.")) return;
    try {
      const { data } = await api.delete(`/invoices/${invoiceId}/payments/${paymentId}`);
      setInvoice(data);
      toast.success("Payment reversed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reverse payment");
    }
  };

  const deleteInvoice = async () => {
    if (!window.confirm("Delete this draft invoice? This cannot be undone.")) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      navigate("/invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const sendByEmail = async () => {
    if (!sendForm.to_email.trim()) return toast.error("Enter a recipient email");
    setSendBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/send-email`, sendForm);
      setInvoice(data);
      if (data.email_sent === false) {
        toast.warning(`Invoice marked as Sent, but email could not be delivered. Add RESEND_API_KEY to your Railway environment to enable email sending.`);
      } else {
        toast.success(`Invoice emailed to ${sendForm.to_email}`);
      }
      setSendOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setSendBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const BASE = process.env.REACT_APP_API_URL || "";
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE}/api/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.invoice_number || invoiceId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const syncToXero = async () => {
    setXeroSyncing(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/sync-xero`);
      toast.success(`Synced to Xero — Invoice ${data.xero_invoice_number || invoiceId}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xero sync failed");
    } finally {
      setXeroSyncing(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      due_date: invoice.due_date?.slice(0, 10) || "",
      purchase_order_number: invoice.purchase_order_number || "",
      notes: invoice.notes || "",
      tax_rate: Math.round((invoice.tax_rate || 0) * 100),
      line_items: (invoice.line_items || []).map(li => ({
        description: li.description,
        quantity: li.quantity,
        unit_price: li.unit_price,
      })),
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${invoiceId}`, {
        due_date: editForm.due_date,
        purchase_order_number: editForm.purchase_order_number,
        notes: editForm.notes,
        tax_rate: (Number(editForm.tax_rate) || 0) / 100,
        line_items: editForm.line_items.filter(li => li.description.trim()),
      });
      setInvoice(data);
      toast.success("Invoice updated");
      setEditOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update invoice");
    } finally {
      setEditBusy(false);
    }
  };

  const updateEditLine = (idx, field, val) => {
    const items = [...editForm.line_items];
    items[idx] = { ...items[idx], [field]: val };
    setEditForm({ ...editForm, line_items: items });
  };

  const canRecordPayment = invoice.lifecycle_status === "sent" && invoice.balance_due > 0;
  const canSend = invoice.lifecycle_status === "draft";
  const canVoid = invoice.lifecycle_status === "sent" && invoice.amount_paid === 0;
  const canDelete = invoice.lifecycle_status === "draft" && (invoice.payments || []).length === 0;

  return (
    <div>
      <PageHeader
        eyebrow={`Invoice · ${invoice.invoice_number}`}
        title={invoice.client_name || "Invoice"}
        description={`Issued ${new Date(invoice.issue_date).toLocaleDateString()} · Due ${new Date(invoice.due_date).toLocaleDateString()}`}
        actions={
          <div className="flex gap-2">
            <Link to="/invoices">
              <Button variant="outline" className="rounded-none border-gray-900" data-testid="back-to-invoices-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> All invoices
              </Button>
            </Link>
            {canSend && (
              <Button onClick={() => transition("sent")} variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900" data-testid="send-invoice-btn">
                <Send className="w-4 h-4 mr-1" /> Mark sent
              </Button>
            )}
            {invoice.lifecycle_status !== "voided" && (
              <Button onClick={() => setSendOpen(true)} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="email-invoice-btn">
                <Mail className="w-4 h-4 mr-1" /> Email to client
              </Button>
            )}
            {canRecordPayment && (
              <Button onClick={() => { setPayForm({ ...payForm, amount: invoice.balance_due.toString() }); setPayOpen(true); }} className="rounded-none bg-emerald-700 hover:bg-emerald-800" data-testid="record-payment-btn">
                <CreditCard className="w-4 h-4 mr-1" /> Record payment
              </Button>
            )}
            {canVoid && (
              <Button onClick={() => transition("voided")} variant="outline" className="rounded-none border-gray-900 text-rose-700" data-testid="void-invoice-btn">
                <XCircle className="w-4 h-4 mr-1" /> Void
              </Button>
            )}
            {xeroConnected && invoice.lifecycle_status !== "voided" && (
              <Button
                onClick={syncToXero}
                disabled={xeroSyncing}
                variant="outline"
                className="rounded-none border-[#00B9A9]/50 text-[#00B9A9] hover:bg-[#00B9A9]/10"
                data-testid="sync-xero-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${xeroSyncing ? "animate-spin" : ""}`} />
                {invoice.xero_invoice_id ? "Re-sync Xero" : "Sync to Xero"}
              </Button>
            )}
            <Button
              onClick={downloadPdf}
              variant="outline"
              className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 print:hidden"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
            {invoice.lifecycle_status !== "voided" && (
              <Button onClick={openEdit} variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 print:hidden" data-testid="edit-invoice-btn">
                <Pencil className="w-4 h-4 mr-1" /> Edit
              </Button>
            )}
            {canDelete && (
              <Button onClick={deleteInvoice} variant="ghost" className="rounded-none text-rose-700 print:hidden" data-testid="delete-invoice-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
          </div>
        }
      />

      {invoice.xero_invoice_id && (
        <div className="px-5 sm:px-8 lg:px-12 py-2 bg-[#00B9A9]/10 border-b border-[#00B9A9]/20 text-xs text-[#00B9A9] flex items-center gap-2" data-testid="xero-sync-banner">
          <RefreshCw className="w-3 h-3" />
          Synced to Xero · ID: <span className="font-mono">{invoice.xero_invoice_id}</span>
          {invoice.xero_synced_at && <span className="text-[#00B9A9]/70 ml-1">· {new Date(invoice.xero_synced_at).toLocaleString()}</span>}
        </div>
      )}

      {/* Status + finance summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#1F2937] border-b border-[#1F2937]">
        <div className="bg-[#0D1117] px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Status</div>
          <span className={`inline-block mt-2 text-xs uppercase tracking-[0.2em] px-2 py-1 ${STATUS_BADGE[invoice.status]}`} data-testid="invoice-detail-status">
            {invoice.status.replace("_", " ")}
          </span>
        </div>
        <div className="bg-[#0D1117] px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Total</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1 font-mono" data-testid="invoice-total">{fmtMoney(invoice.total_amount, ccy)}</div>
        </div>
        <div className="bg-[#0D1117] px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Paid</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1 font-mono text-emerald-700" data-testid="invoice-paid">{fmtMoney(invoice.amount_paid, ccy)}</div>
        </div>
        <div className="bg-[#0D1117] px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Balance due</div>
          <div className={`font-display font-black text-2xl tracking-tighter mt-1 font-mono ${invoice.balance_due > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="invoice-balance">
            {fmtMoney(invoice.balance_due, ccy)}
          </div>
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Line items */}
        <section className="lg:col-span-2">
          <h2 className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-3">Line items</h2>
          <div className="border border-[#1F2937]">
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-[#1F2937] bg-[#030712] text-[10px] uppercase tracking-[0.25em] text-gray-500">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit price</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {(invoice.line_items || []).map((li) => (
              <div key={li.line_item_id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#1F2937] text-sm" data-testid={`detail-line-${li.line_item_id}`}>
                <div className="col-span-6">{li.description}</div>
                <div className="col-span-2 text-right font-mono">{li.quantity}</div>
                <div className="col-span-2 text-right font-mono">{fmtMoney(li.unit_price, ccy)}</div>
                <div className="col-span-2 text-right font-mono font-semibold">{fmtMoney(li.line_total, ccy)}</div>
              </div>
            ))}
            <div className="border-t border-[#374151] px-4 py-3 bg-[#030712]">
              <div className="flex justify-end text-sm space-y-1 font-mono">
                <div className="min-w-[280px] space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmtMoney(invoice.subtotal, ccy)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Tax ({(invoice.tax_rate * 100).toFixed(0)}%)</span><span>{fmtMoney(invoice.tax_amount, ccy)}</span></div>
                  <div className="flex justify-between font-display font-bold text-lg pt-1 border-t border-[#374151]"><span>Total</span><span>{fmtMoney(invoice.total_amount, ccy)}</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payments side panel */}
        <aside>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.25em] text-gray-500">Payments ({(invoice.payments || []).length})</h2>
            {canRecordPayment && (
              <button
                onClick={() => { setPayForm({ ...payForm, amount: invoice.balance_due.toString() }); setPayOpen(true); }}
                className="text-[11px] uppercase tracking-[0.2em] text-[#002FA7] hover:text-[#002480]"
                data-testid="add-payment-link"
              >
                <Plus className="w-3 h-3 inline mr-0.5" /> Add
              </button>
            )}
          </div>
          {(invoice.payments || []).length === 0 ? (
            <div className="border border-dashed border-[#374151] p-6 text-center text-sm text-gray-500">No payments recorded yet.</div>
          ) : (
            <div className="border border-[#1F2937] divide-y divide-gray-100">
              {(invoice.payments || []).map((p) => (
                <div key={p.payment_id} className="px-4 py-3 text-sm" data-testid={`payment-row-${p.payment_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono font-semibold text-emerald-700">{fmtMoney(p.amount, ccy)}</div>
                    <button onClick={() => voidPayment(p.payment_id)} className="text-gray-400 hover:text-rose-600" data-testid={`void-payment-${p.payment_id}`}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {new Date(p.payment_date).toLocaleDateString()} · {p.payment_method}
                  </div>
                  {p.transaction_reference && (
                    <div className="text-[11px] text-gray-400 mt-0.5 font-mono">{p.transaction_reference}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {invoice.purchase_order_number && (
            <div className="mt-6 text-xs text-gray-500">
              <div className="uppercase tracking-[0.25em] text-[10px]">PO Number</div>
              <div className="font-mono mt-1">{invoice.purchase_order_number}</div>
            </div>
          )}
          {invoice.notes && (
            <div className="mt-4 text-xs text-gray-500">
              <div className="uppercase tracking-[0.25em] text-[10px]">Notes</div>
              <div className="mt-1 whitespace-pre-wrap">{invoice.notes}</div>
            </div>
          )}
        </aside>
      </div>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-none border-gray-900 sm:max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Record payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Amount ({ccy})</Label>
              <Input
                type="number"
                step="0.01"
                className="rounded-none mt-1 font-mono"
                value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                data-testid="payment-amount-input"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                Balance due: <span className="font-mono">{fmtMoney(invoice.balance_due, ccy)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Date</Label>
                <Input
                  type="date"
                  className="rounded-none mt-1"
                  value={payForm.payment_date}
                  onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                  data-testid="payment-date-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Method</Label>
                <select
                  value={payForm.payment_method}
                  onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })}
                  className="w-full rounded-none mt-1 h-10 border border-input bg-background px-3 text-sm"
                  data-testid="payment-method-select"
                >
                  <option>Wire Transfer</option>
                  <option>Stripe</option>
                  <option>Card</option>
                  <option>Check</option>
                  <option>Cash</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Transaction reference</Label>
              <Input
                className="rounded-none mt-1 font-mono"
                placeholder="e.g. WIRE-1234"
                value={payForm.transaction_reference}
                onChange={(e) => setPayForm({ ...payForm, transaction_reference: e.target.value })}
                data-testid="payment-ref-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={recordPayment}
              disabled={busy}
              className="rounded-none bg-emerald-700 hover:bg-emerald-800"
              data-testid="confirm-payment-btn"
            >
              {busy ? "Recording…" : "Record payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send by email dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="rounded-none border-[#374151] bg-[#111827] text-white sm:max-w-lg" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#002FA7]" /> Email invoice to client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Recipient email *</Label>
              <Input
                className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white"
                type="email"
                placeholder="client@example.com"
                value={sendForm.to_email}
                onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })}
                data-testid="send-email-to"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Recipient name</Label>
              <Input
                className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white"
                placeholder="Jane Smith"
                value={sendForm.to_name}
                onChange={(e) => setSendForm({ ...sendForm, to_name: e.target.value })}
                data-testid="send-email-name"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Personal message (optional)</Label>
              <textarea
                className="w-full rounded-none mt-1 bg-[#0D1117] border border-[#374151] text-white text-sm px-3 py-2 min-h-[80px] resize-none focus:outline-none focus:border-[#002FA7]"
                placeholder="Hi Jane, please find your invoice attached…"
                value={sendForm.message}
                onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                data-testid="send-email-message"
              />
            </div>
            <p className="text-xs text-[#6B7280]">
              The full invoice with line items will be embedded in the email. Invoice status will update to <strong>Sent</strong> on delivery.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} className="rounded-none border-[#374151] text-[#9CA3AF]">Cancel</Button>
            <Button
              onClick={sendByEmail}
              disabled={sendBusy}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480]"
              data-testid="confirm-send-email-btn"
            >
              <Mail className="w-4 h-4 mr-1" />
              {sendBusy ? "Sending…" : "Send invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit invoice dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Edit invoice</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em]">Due date</Label>
                  <Input type="date" className="rounded-none mt-1" value={editForm.due_date} onChange={e => setEditForm({ ...editForm, due_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em]">Tax rate (%)</Label>
                  <div className="relative mt-1">
                    <Input type="number" step="1" min="0" max="100" className="rounded-none pr-7" value={editForm.tax_rate} onChange={e => setEditForm({ ...editForm, tax_rate: e.target.value })} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em]">PO number</Label>
                  <Input className="rounded-none mt-1" value={editForm.purchase_order_number} onChange={e => setEditForm({ ...editForm, purchase_order_number: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Notes</Label>
                <Textarea className="rounded-none mt-1" rows={3} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] mb-2 block">Line items</Label>
                <div className="border border-[#1F2937] divide-y divide-[#1F2937]">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#030912] text-[10px] uppercase tracking-[0.2em] text-gray-500">
                    <div className="col-span-6">Description</div><div className="col-span-2 text-right">Qty</div><div className="col-span-3 text-right">Unit price</div><div className="col-span-1" />
                  </div>
                  {editForm.line_items.map((li, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                      <input className="col-span-6 bg-transparent text-sm text-white focus:outline-none" value={li.description} onChange={e => updateEditLine(i, "description", e.target.value)} />
                      <input type="number" className="col-span-2 bg-transparent text-sm text-right text-white focus:outline-none" value={li.quantity} onChange={e => updateEditLine(i, "quantity", e.target.value)} />
                      <input type="number" className="col-span-3 bg-transparent text-sm text-right text-white focus:outline-none" value={li.unit_price} onChange={e => updateEditLine(i, "unit_price", e.target.value)} />
                      <button type="button" onClick={() => setEditForm({ ...editForm, line_items: editForm.line_items.filter((_, j) => j !== i) })} className="col-span-1 text-gray-600 hover:text-rose-500 flex justify-end"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <div className="px-3 py-2">
                    <button type="button" onClick={() => setEditForm({ ...editForm, line_items: [...editForm.line_items, { description: "", quantity: 1, unit_price: 0 }] })} className="text-[11px] uppercase tracking-[0.2em] text-[#002FA7] hover:text-[#002480] flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add line
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none border-[#374151] text-[#9CA3AF]">Cancel</Button>
            <Button onClick={saveEdit} disabled={editBusy} className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="save-edit-invoice-btn">
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceDetail;
